/**
 * Theme v2 — color tokens.
 *
 * Sourced from the design prototype's `libertify-theme.ts` (the AntD
 * ThemeConfig the new Knowledge Hub experience was prototyped against)
 * but reorganized into semantic groups instead of flat
 * AntD-token-name-prefixed keys.
 *
 * Used in two places:
 *   1. `libertify-theme-v2.ts` re-maps these onto AntD's `ThemeConfig`
 *      shape for `<ConfigProvider theme={…}>` consumers.
 *   2. Knowledge-hub styled-components import directly:
 *      `import { colors } from '@libertify/ui/theme-v2'`.
 *
 * `as const` is intentional — locks the literal hex values into the
 * type so consumers get autocompletion AND TS catches typos like
 * `colors.accent.bsae`.
 */
export const colors = {
  // ── Brand / accent ─────────────────────────────────────────────────
  // Teal primary used across CTAs, highlights, focus rings.
  accent: {
    base: "#14B8A6",
    hover: "#2DD4BF",
    active: "#0D9488",
    tintSubtle: "rgba(20, 184, 166, 0.04)", // 4% accent — default-state chip / card bg
    tint: "#F0FDFB", // 12% accent — hover-tinted surfaces
    border: "#5EEAD4", // soft teal for outlined elements
    onAccent: "#FFFFFF", // text rendered on top of accent.base
  },

  // ── Surfaces ───────────────────────────────────────────────────────
  // Background layers. `base` is the page; `layout` the app shell tint;
  // `elevated` for popovers / modals / floating cards.
  surface: {
    base: "#FFFFFF",
    layout: "#F0FDFB", // very pale teal — keeps the brand tint everywhere
    elevated: "#FFFFFF",
    muted: "#F9FAFB", // pale gray section / collapsible / panel background
  },

  // ── Text ───────────────────────────────────────────────────────────
  // Four levels of emphasis. `primary` is body copy + headings;
  // `secondary` is supporting; `tertiary` is captions; `quaternary` is
  // disabled / placeholder.
  text: {
    primary: "#090E19",
    secondary: "#6B7280",
    tertiary: "#9CA3AF",
    quaternary: "#D1D5DB",
    onAccent: "#FFFFFF",
  },

  // ── Borders / dividers ─────────────────────────────────────────────
  // `subtle` is the default 1px border on cards/inputs; `muted` is the
  // softer divider between sections; `split` is the AntD-style
  // divider line (same value as muted, kept distinct for semantics).
  border: {
    subtle: "#E5E7EB",
    muted: "#F3F4F6",
    split: "#F3F4F6",
  },

  // ── Status ─────────────────────────────────────────────────────────
  status: {
    success: "#10B981",
    successBg: "#ECFDF5",
    error: "#EF4444",
    errorBg: "#FEF2F2",
    warning: "#F59E0B",
    warningBg: "#FFFBEB",
    info: "#14B8A6", // aliases accent.base on purpose — info IS the brand
    infoBg: "#F0FDFB",
  },
} as const;

export type Colors = typeof colors;
